import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import Button from '../Button';

const ns = 'content-feed';

const ContentFeed = ({
	page = 'default',
	showAllItems = false,
	items = [],
	initialVisibleAmount = 8,
	nextVisibleAmount = 4,
	itemView,
	pagination = false,
	noContainer = false,
	promo = false,
	promoIndex,
	loadButton: { label = 'Load More' } = {},
	onLoadMore,
}) => {
	const isBrowser = typeof window !== 'undefined';
	const isTablet = isBrowser ? window.innerWidth >= 768 : false;

	const [visibleItems, setVisibleItems] = useState([]);
	const [numberOfVisibleItems, setNumberOfVisibleItems] = useState(initialVisibleAmount);
	const [currentPage, setCurrentPage] = useState(1);
	const [paginationTransition, setPaginationTransition] = useState(false);

	const rootClassnames = classNames({
		[`${ns}`]: true,
		[`${ns}__${page}`]: page,
	});

	const indexOfLastPost = currentPage * initialVisibleAmount;
	const indexOfFirstPost = indexOfLastPost - initialVisibleAmount;

	const paginate = useCallback(
		(pageNumber) => {
			setPaginationTransition(true);
			setCurrentPage(pageNumber);
			setTimeout(() => {
				setPaginationTransition(false);
			}, 750);
		},
		[setPaginationTransition, setCurrentPage]
	);

	const paginationTransitionClass = pagination && paginationTransition ? 'pagination-transition' : '';

	useEffect(() => {
		const visibleItemsToSet = [...items];

		if (pagination) {
			setTimeout(() => {
				setVisibleItems(items.slice(indexOfFirstPost, indexOfLastPost));
			}, paginationTransition ? 750 : 0);
		}

		if (promo && !isTablet) {
			visibleItemsToSet.splice(promoIndex, 0, { promo: true });
		}

		setVisibleItems(visibleItemsToSet);

		const visibleItemsLength = showAllItems ? items.length : initialVisibleAmount;
		setNumberOfVisibleItems(promo && !isTablet ? visibleItemsLength + 1 : visibleItemsLength);
	}, [
		items,
		initialVisibleAmount,
		indexOfFirstPost,
		indexOfLastPost,
		pagination,
		paginationTransition,
		promo,
		isTablet,
		showAllItems,
		promoIndex,
	]);

	useEffect(() => {
		setCurrentPage(1);
	}, [items]);

	const handleLoadMore = useCallback(() => {
		const updatedNumberOfVisibleItems = numberOfVisibleItems + nextVisibleAmount;
		setNumberOfVisibleItems(updatedNumberOfVisibleItems);
		onLoadMore(updatedNumberOfVisibleItems);
	}, [onLoadMore]);

	const renderContentFeed = useCallback(() => {
		return visibleItems.slice(0, numberOfVisibleItems).map((item) => (
			<React.Fragment key={`${item.id}`}>{itemView(item, numberOfVisibleItems)}</React.Fragment>
		));
	}, [visibleItems, numberOfVisibleItems, itemView]);

	return (
		<div className={rootClassnames}>
			<div className={!noContainer ? 'container' : ''}>
				{items.length ? (
					<div className={`${ns}__list ${paginationTransitionClass}`}>
						{renderContentFeed()}
					</div>
				) : (
					<div className={`${ns}__no-items h3`}>Sorry, we couldn't find any results.</div>
				)}
				{label && !pagination && numberOfVisibleItems < visibleItems.length && (
					<div className={`${ns}__load-more`}>
						<Button onClick={handleLoadMore}>{label}</Button>
					</div>
				)}
				{pagination && items.length > initialVisibleAmount && (
					<div className={`${ns}__pagination`}>
						{Array.from({ length: Math.ceil(items.length / initialVisibleAmount) }, (_, i) => (
							<div key={`pagination-item-${i}`} className={`${ns}__pagination-item`}>
								<button onClick={() => paginate(i + 1)} className={i + 1 === currentPage ? 'active' : ''}>
									{i + 1}
								</button>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default ContentFeed;
